import React, {useState} from 'react'
import {ValueInputWithRange} from '../../../sharedComponents'
import {useTranslation} from 'react-i18next'

import AromiDishSelector, {isAromiDishTask} from './AromiDishSelector'
import InputWithTags from './InputWithTags'
import PerformTaskForm from './PerformTaskForm'
import {useAppState} from '../../../state'

import {convertUnit, tempUnitStr, handleFloatInput, normalizeSites} from '../../../config/utils'
import {EventStatusCode, PerformableTask} from '../../../state/performTask/state'
import PerformTaskTitle from './PerformTaskTitle'
import {FormContainer} from './PerformCoolerCoolingTask'

interface Props {
  task: PerformableTask
  setValue: any
  onAssetSaved: () => void
  onAssetRemoved: (assetId: string) => void
  payload: any
  handleRemove: () => void
  handleNumberBlur: (event: any) => void
  hasCurrentEvent?: boolean
  currentEventId?: string
}

const PerformTemperatureTask = ({
  task,
  setValue,
  onAssetSaved,
  onAssetRemoved,
  payload,
  handleRemove,
  handleNumberBlur,
  hasCurrentEvent,
  currentEventId
}: Props) => {
  const {state} = useAppState()
  const {t} = useTranslation('tasks')
  const [isRequired, setIsRequired] = useState(true)
  const selectedChain = state.selectedChainId ? (state.chainsById as any)?.[state.selectedChainId] : undefined
  const currentSite =
    state.site?.id && selectedChain
      ? normalizeSites(selectedChain).find((candidate: any) => candidate.id === state.site?.id) || state.site
      : state.site
  const allowAromiAlternative = isAromiDishTask(task.taskGroup.name) && Boolean(currentSite?.aromiIntegration)
  const handleRemoveTask = () => {
    setIsRequired(false)
    if (handleRemove) {
      handleRemove()
    }
  }

  const isFoodDelivery = task.taskGroup.name === 'Food delivery' && task.taskGroup.fixed
  const isRecivingTask = task.taskGroup.name === 'Receiving' && task.taskGroup.fixed

  return (
    <FormContainer>
      <PerformTaskTitle task={task} />
      <ValueInputWithRange
        name="value-input"
        id="value-input"
        value={payload.value}
        label={t('tasks:labels.measuredTemperature', 'Measured temperature')}
        onChange={e => setValue('value', handleFloatInput(e.target.value))}
        onBlur={e => handleNumberBlur(e.target.value)}
        unit={tempUnitStr(state.site!.temperatureUnit)}
        minValue={convertUnit(state.site!, task.minValue)}
        maxValue={convertUnit(state.site!, task.maxValue)}
        message={{
          success: t('tasks:inputWithRange.withinTarget', 'The value is within target limits'),
          error: t('tasks:inputWithRange.not.withInTarget', 'The value is not within target')
        }}
        required={isRequired}
      />
      <InputWithTags
        savedSuggestions={payload.suggestions}
        defaultSuggestions={task.defaultSuggestions || []}
        label={t('tasks:labels.measurementTarget', 'Measurement target')}
        placeholder={t('tasks:placeholder.measurementTarget', 'Measurement target')}
        onChange={setValue}
        required={isRequired && !allowAromiAlternative}
        maxCharacters={100}
      />
      <AromiDishSelector task={task} payload={payload} setValue={setValue} />
      {isFoodDelivery && (
        <InputWithTags
          keyName={'reportedRecipient'}
          savedSuggestions={payload.reportedRecipient}
          defaultSuggestions={task.defaultRecipients || []}
          onChange={setValue}
          label={t('tasks:labels.recipient', 'Recipients')}
          placeholder={t('tasks:labels.recipient', 'Recipients')}
          required={false}
        />
      )}
      {isRecivingTask && (
        <InputWithTags
          keyName={'reportedSupplier'}
          savedSuggestions={payload.reportedSupplier}
          defaultSuggestions={task.defaultSuppliers || []}
          onChange={setValue}
          label={t('tasks:labels.supplier', 'Suppliers')}
          placeholder={t('tasks:placeholder.supplier', 'Suppliers')}
          required={false}
        />
      )}
      <PerformTaskForm
        onInputChange={setValue}
        onAssetSaved={onAssetSaved}
        onAssetRemoved={onAssetRemoved}
        payload={payload}
        handleRemove={
          !!task.scheduleInstance || task.status === EventStatusCode.IN_PROGRESS ? handleRemoveTask : undefined
        }
        setValue={setValue}
        confirm={
          task.scheduleInstance || task.status === EventStatusCode.IN_PROGRESS
            ? t(
                'tasks:labels.confirmRemoveTask',
                'Are you sure you want to delete this task? This will also remove any associated resolved alarms.'
              )
            : undefined
        }
        hasCurrentEvent={!!hasCurrentEvent}
        currentEventId={currentEventId}
      />
    </FormContainer>
  )
}

export default PerformTemperatureTask
